import React, { useState, useEffect } from 'react';
import { CheckSquare, Square } from 'lucide-react';

const ImportantNoticeModal = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  useEffect(() => {
    // Check if user has already acknowledged notice in this session
    const acknowledged = sessionStorage.getItem('quanentry_notice_acknowledged');
    if (acknowledged !== 'true') {
      setIsVisible(true);
    }
  }, []);

  // Block ESC key from closing the modal
  useEffect(() => {
    if (!isVisible) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isVisible]);

  if (!isVisible) return null;

  const handleProceed = () => {
    if (!isChecked) return;
    sessionStorage.setItem('quanentry_notice_acknowledged', 'true');
    setIsVisible(false);
  };

  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4 sm:p-6 bg-black/85 backdrop-blur-md transition-opacity duration-300"
      onClick={(e) => e.stopPropagation()}
    >
      <div 
        className="relative w-full max-w-[720px] max-h-[90vh] bg-[#0B0E14] border border-[#1E293B] rounded-xl shadow-2xl flex flex-col p-6 sm:p-8 text-white animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Institutional Header - ONLY OFFICIAL NOTICE */}
        <div className="flex flex-col items-center text-center pb-2 mb-4">
          <div className="inline-flex items-center justify-center text-[13px] font-semibold tracking-[0.18em] uppercase text-[#8FE3CC] bg-[#123A35] px-4 py-1.5 rounded-full border border-[#2C5E56]">
            OFFICIAL NOTICE
          </div>
        </div>

        {/* Modal Content - Left-aligned, comfortable line-height, distinct paragraph separation */}
        <div className="overflow-y-auto pr-1 pb-4 custom-modal-scrollbar">
          <div className="max-w-[620px] mx-auto space-y-5 text-sm sm:text-[15px] leading-[1.7] text-gray-300 text-left">
            <p className="text-white/95 font-normal">
              Quanentry Capital Private Limited is an independent proprietary trading and quantitative research firm dedicated to developing systematic investment strategies through rigorous analysis, advanced technology, and disciplined risk management. Every trading and investment activity undertaken by the Company is conducted exclusively with its own capital, reflecting a long-standing commitment to institutional excellence, integrity, and prudent risk governance.
            </p>

            <p className="text-gray-300 font-normal">
              This website serves as the official digital platform of Quanentry Capital Private Limited, offering key insights into the Company’s core business, research capabilities, corporate philosophy, career opportunities, and regulatory disclosures. It is established to provide an authentic, transparent overview of the institution, its operational purpose, and its long-term corporate vision.
            </p>

            <p className="text-gray-300 font-normal">
              Quanentry Capital Private Limited operates entirely with its own capital and does not manage third-party assets, accept public funds, or provide client-facing financial services. Information contained across this website is presented solely to convey an accurate overview of the Company’s operations and activities, and should not be construed as investment advice, an offer, or a solicitation for any financial transaction.
            </p>

            <p className="font-medium text-white/90 pt-1 text-sm sm:text-[14px]">
              By proceeding, you acknowledge that you have read and understood this notice.
            </p>
          </div>
        </div>

        {/* Single subtle divider & Footer */}
        <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4 max-w-[620px] mx-auto w-full">
          <label 
            className="flex items-center gap-3 cursor-pointer select-none group text-sm text-gray-200"
            onClick={() => setIsChecked(!isChecked)}
          >
            <div className="text-[#00FFD1] transition-transform duration-150 group-hover:scale-105 shrink-0">
              {isChecked ? (
                <CheckSquare size={20} className="text-[#00FFD1]" />
              ) : (
                <Square size={20} className="text-gray-500 group-hover:text-gray-300" />
              )}
            </div>
            <span>I have read and understood the above notice.</span>
          </label>

          <button
            onClick={handleProceed}
            disabled={!isChecked}
            className={`w-full sm:w-auto px-7 py-3 rounded-lg font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 shrink-0 ${
              isChecked
                ? 'bg-[#00FFD1] text-black hover:bg-[#00e6bd] shadow-lg shadow-[#00FFD1]/20 cursor-pointer font-semibold'
                : 'bg-white/10 text-gray-500 cursor-not-allowed border border-white/5'
            }`}
          >
            Proceed to Website
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportantNoticeModal;
